<template>
  <div class="flex gap-2">
    <VaIconMenuCollapsed
      class="cursor-pointer"
      :class="{ 'x-flip': !isSidebarMinimized }"
      :color="collapseIconColor"
      @click="isSidebarMinimized = !isSidebarMinimized"
    />

    <nav class="flex items-center">
      <VaBreadcrumbs>
        <VaBreadcrumbsItem label="Home" :to="{ name: 'reference-design' }" />
        <VaBreadcrumbsItem
          v-for="item in items"
          :key="item.label"
          :label="item.label"
          :class="[{ 'cursor-pointer': !!item.to }]"
          @click="handleBreadcrumbClick(item)"
        />
      </VaBreadcrumbs>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/globalStore'
import NavigationRoutes from '../sidebar/NavigationRoutes'

const globalStore = useGlobalStore()
const { isSidebarMinimized, dynamicBreadcrumb } = storeToRefs(globalStore)

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const router of routers) {
      if (router.name === name) {
        return router.displayName
      }
      if (router.children) {
        const result = traverse(router.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const items = computed(() => {
  const result: { label: string; to: string; hasChildren: boolean }[] = []

  route.matched.forEach((routeRecord, index) => {
    let labelKey = (routeRecord.meta?.breadcrumb as string) || findRouteName(routeRecord.name as string)

    const isLast = index === route.matched.length - 1
    if (isLast && labelKey === 'Detail' && dynamicBreadcrumb.value) {
      labelKey = dynamicBreadcrumb.value
    }

    if (!labelKey) return

    result.push({
      label: t(labelKey),
      to: routeRecord.path,
      hasChildren: routeRecord.children && routeRecord.children.length > 0,
    })
  })

  // handle virtual parent recursively
  const current = route.matched[route.matched.length - 1]
  if (current.meta?.parent) {
    const parentChain: { label: string; to: string; hasChildren: boolean }[] = []
    let currentParentName = current.meta.parent as string
    const existingPaths = new Set(result.map((r) => r.to))

    // Recursively build parent chain
    while (currentParentName) {
      const parentRoute = router.resolve({ name: currentParentName })
      const labelKey = (parentRoute.meta?.breadcrumb as string) || findRouteName(currentParentName)

      if (labelKey && !existingPaths.has(parentRoute.path)) {
        parentChain.unshift({
          label: t(labelKey),
          to: parentRoute.path,
          hasChildren: false,
        })
        existingPaths.add(parentRoute.path)
      }
      // Check if this parent has a parent
      currentParentName = parentRoute.meta?.parent as string
    }

    // Insert all parents before the last item
    if (parentChain.length > 0) {
      result.splice(result.length - 1, 0, ...parentChain)
    }
  }

  if (result.length > 0) {
    result[result.length - 1].to = ''
  }

  return result
})

const { getColor } = useColors()

const collapseIconColor = computed(() => getColor('secondary'))

const handleBreadcrumbClick = (item: BreadcrumbNavigationItem) => {
  if (!item.hasChildren) {
    router.push(item.to)
  }
}
</script>

<style lang="scss" scoped>
.x-flip {
  transform: scaleX(-100%);
}
</style>
